<template>
  <div>

    <div class="box" :class="{ 'py-1 px-1': compact }">
      <div class="flex flex-wrap items-center" v-if="showActions">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getReminders" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <div
            v-if="hasPermission('reminder.create')"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="$router.push('/reminder/create/')"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New Reminder</span>
          </div>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getReminders">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>

      </div>

      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <reminder-filters
            :filters.sync="filters"
            @updated="getReminders"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasReminders">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th v-if="!compact">#</th>
                  <th>Recipient</th>
                  <th>Method</th>
                  <th>Subject</th>
                  <th v-if="!compact">Content</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(reminder, i) in reminders" :key="i">
                  <td class="font-bold" v-if="!compact">{{ reminder.id }}</td>
                  <td>
                    <template v-if="!customerID && reminder.contact">
                      <p class="text-xs">Individual</p>
                      <a class="text-link" @click.stop="$router.push('/customer/details/' + reminder.contact.id)">
                        <span v-if="!compact">
                          #{{ reminder.contact.id }}
                        </span>
                        {{ reminder.contact.first_name }}
                        {{ reminder.contact.last_name }}
                        <span v-if="!compact && reminder.contact.company_name">
                          ({{ reminder.contact.company_name }})
                        </span>
                      </a>
                    </template>
                    <template v-else-if="reminder.list">
                      <p class="text-xs">List</p>
                      <a class="text-link" @click.stop="$router.push('/reminder-list/edit/' + reminder.list.id)">
                        <span v-if="!compact">
                          #{{ reminder.list.id }}
                        </span>
                        {{ reminder.list.name }}
                      </a>
                    </template>
                  </td>
                  <td>
                    <p
                      v-if="!reminder.list"
                      class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                      :class="reminder.method === 'sms' ? 'bg-pink-500' : 'bg-green-500'"
                    >{{ reminder.method }}</p>
                  </td>
                  <td>
                    {{ reminder.subject || '-' }}
                  </td>
                  <td v-if="!compact">
                    <template v-if="reminder.content.length > 20">
                      {{ reminder.content.substring(0, 20) }}[...]
                    </template>
                    <template v-else>
                      {{ reminder.content }}
                    </template>
                  </td>
                  <td class="text-xs">
                    {{ $moment(reminder.due_date).format(config.dateTimeFormats.display) }}
                  </td>
                  <td>
                    <span class="font-bold text-xs text-white p-1 rounded text-center bg-green-500" v-if="reminder.date_sent">
                      <span
                        v-tooltip="{ content: $moment(reminder.date_sent).format(config.dateTimeFormats.display) }"
                      >SENT</span>
                    </span>
                    <span class="font-bold text-xs text-white p-1 rounded text-center bg-orange-500" v-else-if="reminder.isFutureDate">PENDING</span>
                    <span class="font-bold text-xs text-white p-1 rounded text-center bg-red-500" v-else>NOT SENT</span>
                  </td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/reminder/details/' + reminder.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/reminder/edit/' + reminder.id)"
                        v-if="!reminder.date_sent && reminder.isFutureDate"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteReminder(reminder.id)"
                        v-if="!reminder.date_sent"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasReminders && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>


  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const ReminderFilters = resolve => require(['@/components/filters/Filters'], resolve)
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  props: {
    customerID: {},
    showActions: {
      type: Boolean,
      default: true,
    },
    method: String,
    compact: Boolean,
  },
  components: {
    FilteringToggleButton,
    ReminderFilters,
    ListSearch,
    PaginationComponent,
  },
  data() {
    return {
      reminders: [],
      searchInput: '',
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        method: {
          key: 'method',
          name: 'Method',
          active: true,
          options: () => this.config.reminderMethods.map(m => { return {value: m, class: (m === 'sms' ? 'bg-pink-500 text-black' : 'bg-green-500')} } ),
          value: null,
        },
        dueDate: {
          key: 'due_date',
          name: 'Due date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
        status: {
          key: 'status',
          name: 'Status',
          active: true,
          options: [
            {value: 'sent', class: 'bg-green'},
            {value: 'pending', class: 'bg-orange text-black'},
            {value: 'not-sent', class: 'bg-red text-black'},
          ],
          value: null,
        },
      },
      query: {
        search: '',
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getReminders()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getReminders()
      }
    },
  },
  computed: {
    hasReminders() {
      return Object.entries(this.reminders).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.getReminders()
  },
  methods: {
    deleteReminder(reminderID) {
      this.$swal.fire({
        title: 'Delete reminder',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('reminders/' + reminderID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getReminders()
              })
          }
        })
    },
    getReminders() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.due_date) {
        let dueDate = this.$getRangeDates(filters.due_date, this.config)
        filters.dueDateFrom = dueDate.from
        filters.dueDateTo = dueDate.to
      }

      axios.get('reminders', {params: {
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.reminders = Object.assign([], res.data)
            this.reminders.map(r => r.isFutureDate = this.$moment().isBefore(r.due_date))

            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
